import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { BulkProviderChangeProjectTableData, ProjectView } from "@app/shared/provider-change-wizards/interfaces";
import { wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { ManageResourceTagDisplayModel } from "@app/tags/types";
import { groupBy } from "lodash";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

export interface TagGroupData {
  [wafflesKeyColumnName]: string;
  tagGroupName: string;
  tagName: string,
  numberOfProjects: number;
  projectIds?: string[];
  projectNames?: string[];
  hiddenTagGroupName?: string;
  rowIsDisabled?: boolean;
}

type TagExpansionData = Map<string, TagGroupData[]>;

interface BulkProviderChangeSelectProjectsByTagResponse {
  getEligibleSelectedProjectIds: (totalSelectedProjectIds: string[]) => string[];
  handleTagCheckboxChange: (selectedKeys: string[], isBulk?: boolean) => void;
  indeterminateTags: string[];
  isTagApiCallResolved: boolean;
  renderedExpansionData: TagExpansionData;
  renderedTags: TagGroupData[];
  selectedTags: string[];
  tagExpansionData: TagExpansionData;
  tagGroupData: TagGroupData[];
}

const resourceTaggingService = new ResourceTaggingService();
export const useSelectProjectByTags = (
  projectsData: BulkProviderChangeProjectTableData[],
  searchString: string,
  filteredView: ProjectView,
  totalSelectedProjectIds: string[],
  setEligibleSelectedProjectIds: Dispatch<SetStateAction<string[]>>,
  setTotalSelectedProjectIds: Dispatch<SetStateAction<string[]>>,
): BulkProviderChangeSelectProjectsByTagResponse => {
  const tagsResponseRef = useRef<ManageResourceTagDisplayModel[]>(null);
  const [tagGroupData, setTagGroupData] = useState<TagGroupData[]>([]);
  const [tagExpansionData, setTagExpansionData] = useState<TagExpansionData>(new Map());
  const [tagsToProjectsLookup, setTagsToProjectsLookup] = useState<{ [key: string]: string[] }>({});
  const [isTagApiCallResolved, setIsTagApiCallResolved] = useState(false);

  const { indeterminateTags, selectedTags } = useMemo(() => {
    const indeterminateTags: string[] = [];
    const selectedTags: string[] = [];

    Object.entries(tagsToProjectsLookup).forEach(([tag, projectIds]) => {
      // [].every() always returns true, so we have to check the length
      if (projectIds.length > 0 && projectIds.every((p) => totalSelectedProjectIds.includes(p))) {
        selectedTags.push(tag);
      } else if (projectIds.some((p) => totalSelectedProjectIds.includes(p))) {
        indeterminateTags.push(tag);
      }
    });

    return { indeterminateTags, selectedTags };
  }, [totalSelectedProjectIds, tagGroupData, projectsData]);

  const disabledProjectIds = useMemo(() => {
    return projectsData.filter((project) => project.rowIsDisabled).map((project) => project.projectId.toString());
  }, [projectsData]);

  useEffect(() => {
    // We mutate the response based on the projectsData, 
    // but the response itself doesn't change. So we're good to save it the first time
    // and reuse it to reduce API calls.
    if (tagsResponseRef.current) {
      handleTagsUpdate(tagsResponseRef.current);
    } else {
      setIsTagApiCallResolved(false);
      resourceTaggingService.getClientProjectGroupTagInfo()
        .then((response) => handleTagsUpdate(response ?? []))
        .catch(() => handleTagsUpdate([]));
    }
  }, [projectsData]);

  const handleTagsUpdate = (tagsResponse: ManageResourceTagDisplayModel[]) => {
    tagsResponseRef.current = tagsResponse;
    const visibleTags = tagsResponse.filter((t) => projectsData.find((d) => d.projectName === t.projectName));
    const newTagGroupData: TagGroupData[] = [];
    const newTagExpansionData: TagExpansionData = new Map();
    const groupedTagGroups = groupBy(visibleTags, (v) => v.tagGroupName);
    const newTagsToProjectsLookup: Record<string, string[]> = {};

    const projectNamesToIds: Record<string, number> = {};
    projectsData.forEach((project) => {
      projectNamesToIds[project.projectName] = project.projectId;
    });

    Object.entries(groupedTagGroups).forEach(([groupKey, tags]) => {
      let totalNumberOfProjects = 0;
      /* oxlint-disable sort-keys */
      newTagGroupData.push({
        tagGroupName: groupKey,
        tagName: "",
        numberOfProjects: tags.length,
        [wafflesKeyColumnName]: groupKey,
      });
      newTagExpansionData.set(groupKey, []);
      const groupedTags = groupBy(tags, (t) => t.tagName);
      Object.entries(groupedTags).forEach(([tagKey, values]) => {
        const projectNames = values.map((value) => value.projectName);
        const projectIds = projectNames.map((p) => projectNamesToIds[p]?.toString()).filter((x) => !!x);
        newTagExpansionData.set(groupKey, [
          ...newTagExpansionData.get(groupKey),
          {
            tagGroupName: "",
            hiddenTagGroupName: groupKey,
            tagName: tagKey,
            numberOfProjects: projectNames.length,
            projectIds: projectIds,
            projectNames: projectNames,
            rowIsDisabled: projectIds.length === 0,
            [wafflesKeyColumnName]: `${groupKey}:${tagKey}`,
          }
        ]);
        totalNumberOfProjects += projectNames.length;
        /* oxlint-enable sort-keys */

        // we include disabled projects here to get an accurate read on the tag's checkbox
        // i.e. if tag's projects are ALL disabled, no projects would be checked, and the tag wouldn't be checked, which is confusing
        // however, a disabled project's checkbox will NOT show as checked. that's a long-standing Waffles thing.
        // we store both totalCheckedProjects and eligibleCheckedProjects in wizard state
        newTagsToProjectsLookup[`${groupKey}:${tagKey}`] = projectIds;
      })
      newTagGroupData[newTagGroupData.length - 1].numberOfProjects = totalNumberOfProjects;
    })

    setTagGroupData(newTagGroupData);
    setTagExpansionData(newTagExpansionData);
    setTagsToProjectsLookup(newTagsToProjectsLookup);
    setIsTagApiCallResolved(true);
  };

  const { renderedTags, renderedExpansionData } = useMemo(() => {
    if (!searchString) {
      return { renderedExpansionData: tagExpansionData, renderedTags: tagGroupData };
    }

    const regex = new RegExp(searchString, "i");
    let filteredTagList: TagGroupData[] = [];
    const newExpansionData: TagExpansionData = new Map();
    if (filteredView === "tags") {
      tagExpansionData.forEach((value, key) => {
        const filtered = value.filter((v) => v.tagName.match(regex));
        if (filtered.length > 0) {
          newExpansionData.set(key, filtered);
        }
      });
      filteredTagList = tagGroupData.filter((tagGroup) => newExpansionData.has(tagGroup.tagGroupName));
    } else if (filteredView === "projects") {
      tagExpansionData.forEach((value, key) => {
        const filtered = value.filter((v) => v.projectNames.filter((n) => n.match(regex)).length > 0);
        if (filtered.length > 0) {
          const tagGroupNames = filtered.map((f) => f.hiddenTagGroupName);
          const tgDataRecord = tagGroupData.filter((tg) => tagGroupNames.includes(tg.tagGroupName));
          newExpansionData.set(key, filtered);
          filteredTagList = filteredTagList.concat(tgDataRecord);
        }
      })
    }
    return {
      renderedExpansionData: newExpansionData,
      renderedTags: filteredTagList,
    };
  }, [searchString, tagGroupData, projectsData, filteredView]);

  // tags table doesn't have a bulk checkbox
  const handleTagCheckboxChange = (newSelectedTags: string[]) => {
    let associatedProjects: string[] = [];
    // figure out if one was added or removed, and get the one that changed
    if (newSelectedTags.length > selectedTags.length) {
      // add
      const changedTag = newSelectedTags.filter((tag) => !selectedTags.includes(tag))[0];
      associatedProjects = tagsToProjectsLookup[changedTag];
      const newTotalSelectedProjectIds = Array.from(new Set(totalSelectedProjectIds.concat(associatedProjects)));
      setTotalSelectedProjectIds(newTotalSelectedProjectIds);
      setEligibleSelectedProjectIds(getEligibleSelectedProjectIds(newTotalSelectedProjectIds));
    } else {
      // remove
      const changedTag = selectedTags.filter((tag) => !newSelectedTags.includes(tag))[0];
      associatedProjects = tagsToProjectsLookup[changedTag];
      const newTotalSelectedProjectIds = totalSelectedProjectIds.filter((id) => !associatedProjects.includes(id));
      setTotalSelectedProjectIds(newTotalSelectedProjectIds);
      setEligibleSelectedProjectIds(getEligibleSelectedProjectIds(newTotalSelectedProjectIds));
    }
  }

  const getEligibleSelectedProjectIds = (totalSelectedProjectIds: string[]) => {
    return totalSelectedProjectIds.filter((id) => !disabledProjectIds.includes(id));
  }

  return {
    getEligibleSelectedProjectIds,
    handleTagCheckboxChange,
    indeterminateTags,
    isTagApiCallResolved,
    renderedExpansionData,
    renderedTags,
    selectedTags,
    tagExpansionData,
    tagGroupData,
  }
}